/**
 * "Sync from ERP" — one item's record, prices and stock, refreshed from the ERP on demand
 * (Akhil, 2026-10-26: "price updated for the item is not syncing immediately / price 0 — out of
 * stock").
 *
 * The connector's `pull_single_record {quoflow_doctype, record_name}` refreshes ONE existing local
 * record. Measured on rfqdev: `Item`, `Item Price` and `Bin` each answer `{status: 'updated'}`; an
 * unknown name answers "not found on remote". An item's price lives on its `Item Price` rows and its
 * stock on its `Bin` rows, not on the item, so a sync is all three. A price row the ERP has but
 * this site has never seen cannot be pulled by name — that needs the connector (ask `D47`).
 *
 * Like `pull_item_uom`, this is the one other method outside `quoflow.*` — allowed on POST alone
 * by the proxy (`server/policy.ts`).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { postDotted } from './bosun'
import { getResource } from './frappe'
import { str } from './wire'

export const PULL_SINGLE_RECORD_METHOD = 'quoflow_connector.quoflow_connector.api.pull_single_record'

export type PullDoctype = 'Item' | 'Item Price' | 'Bin' | 'Supplier' | 'Contact'
export interface PullRequest {
  quoflow_doctype: PullDoctype
  record_name: string
}

/** The pulls that refresh one item: the item first, then each price row and stock row. */
export function pullsForItem(code: string, priceNames: readonly string[], binNames: readonly string[]): PullRequest[] {
  const clean = (names: readonly string[]) => [...new Set(names.map((n) => n.trim()).filter((n) => n !== ''))]
  return [
    { quoflow_doctype: 'Item', record_name: code },
    ...clean(priceNames).map((n) => ({ quoflow_doctype: 'Item Price' as const, record_name: n })),
    ...clean(binNames).map((n) => ({ quoflow_doctype: 'Bin' as const, record_name: n })),
  ]
}

export interface SyncResult {
  codes: string[]
  pulled: number
  /** `doctype name` for each pull the connector refused, with its reason. */
  failed: string[]
}

async function namesFor(doctype: 'Item Price' | 'Bin', code: string): Promise<string[]> {
  const page =
    doctype === 'Item Price'
      ? await getResource<Record<string, unknown>>('Item Price', { fields: ['name'], filters: [['item_code', '=', code]], limit: 0 })
      : await getResource<Record<string, unknown>>('Bin', { fields: ['name'], filters: [['item_code', '=', code]], limit: 0 })
  return page.data.map((row) => str(row.name))
}

/**
 * Runs each key's pulls, three keys at a time — each pull is ~1s, and forty of them in a row would
 * keep a purchaser waiting minutes. Never throws for one bad record; refusals are collected.
 */
async function runPulls(keys: readonly string[], plan: (key: string) => Promise<PullRequest[]>): Promise<SyncResult> {
  const unique = [...new Set(keys.filter(Boolean))]
  const result: SyncResult = { codes: unique, pulled: 0, failed: [] }
  const one = async (key: string) => {
    for (const pull of await plan(key)) {
      try {
        await postDotted<unknown>(PULL_SINGLE_RECORD_METHOD, pull)
        result.pulled += 1
      } catch (error) {
        result.failed.push(`${pull.quoflow_doctype} ${pull.record_name}: ${error instanceof Error ? error.message : String(error)}`)
      }
    }
  }
  const queue = [...unique]
  await Promise.all(Array.from({ length: Math.min(3, queue.length) }, async () => {
    for (let key = queue.shift(); key !== undefined; key = queue.shift()) await one(key)
  }))
  return result
}

/** Refreshes every given item — record, prices, stock — from the ERP. */
export function syncItemsFromErp(codes: readonly string[]): Promise<SyncResult> {
  return runPulls(codes, async (code) => {
    const [prices, bins] = await Promise.all([namesFor('Item Price', code).catch(() => []), namesFor('Bin', code).catch(() => [])])
    return pullsForItem(code, prices, bins)
  })
}

/* ---------- suppliers ---------- */

/**
 * The pulls that refresh one supplier's addresses (2026-10-26: "sync for supplier mails"). The
 * address the RFQ mail goes to is read from the Supplier and from its Contact rows
 * (`supplierContacts.ts`), so both are pulled — each by name, measured on `7H Trading ltd`.
 */
export function pullsForSupplier(supplier: string, contactNames: readonly string[]): PullRequest[] {
  const contacts = [...new Set(contactNames.map((n) => n.trim()).filter((n) => n !== ''))]
  return [
    { quoflow_doctype: 'Supplier', record_name: supplier },
    ...contacts.map((n) => ({ quoflow_doctype: 'Contact' as const, record_name: n })),
  ]
}

async function contactNamesOf(supplier: string): Promise<string[]> {
  const page = await getResource<Record<string, unknown>>('Contact', {
    fields: ['name'],
    filters: [
      ['Dynamic Link', 'link_name', '=', supplier],
      ['Dynamic Link', 'link_doctype', '=', 'Supplier'],
    ],
    limit: 20,
  })
  return page.data.map((row) => str(row.name))
}

/** Refreshes every given supplier and its contacts from the ERP. */
export function syncSuppliersFromErp(suppliers: readonly string[]): Promise<SyncResult> {
  return runPulls(suppliers, async (supplier) => pullsForSupplier(supplier, await contactNamesOf(supplier).catch(() => [])))
}

/** The supplier sync, with every address read refetched afterwards. */
export function useSyncSuppliersFromErp(options?: { onDone?: (result: SyncResult) => void; onError?: (error: Error) => void }) {
  const queryClient = useQueryClient()
  return useMutation<SyncResult, Error, string[]>({
    mutationFn: syncSuppliersFromErp,
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['items', 'supplier-emails'] }),
        queryClient.invalidateQueries({ queryKey: ['items', 'supplier-email'] }),
        queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
      ])
      options?.onDone?.(result)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/** The mutation the RFQ page uses — and afterwards every item, price and stock read is refetched. */
export function useSyncItemsFromErp(options?: { onDone?: (result: SyncResult) => void; onError?: (error: Error) => void }) {
  const queryClient = useQueryClient()
  return useMutation<SyncResult, Error, string[]>({
    mutationFn: syncItemsFromErp,
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['items'] }),
        queryClient.invalidateQueries({ queryKey: ['item-price-rows'] }),
        queryClient.invalidateQueries({ queryKey: ['item-prices'] }),
      ])
      options?.onDone?.(result)
    },
    onError: (error) => options?.onError?.(error),
  })
}
